import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { motion, useAnimation, PanInfo } from "framer-motion";
import { colors, transitions } from "../../styles/theme";
import { PeekChildrenCollapsed } from "./LayoutMobile.styled";
import {
  DRAWER_PICK_AREA_HEIGHT,
  DRAWER_PICK_HEIGHT_COLLAPSED,
} from "../../constants";

type BottomDrawerTogglePayload = {
  open: boolean;
  height: number;
};

type BottomDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onToggle?: (payload: BottomDrawerTogglePayload) => void;
  peekHeight?: number;
  children?: React.ReactNode;
  childrenCollapsed?: React.ReactNode;
  hidden?: boolean;
};

const DRAWER_LINES_HEIGHT = 8;

export const BottomDrawer: React.FC<BottomDrawerProps> = (props) => {
  const {
    open,
    onOpenChange,
    onToggle,
    peekHeight = 60,
    children,
    childrenCollapsed,
  } = props;
  const controls = useAnimation();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const [headerHeight, setHeaderHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    const measure = () => {
      const h = el.offsetHeight || peekHeight;
      setHeaderHeight(h);
    };

    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(el);

    return () => ro.disconnect();
  }, [peekHeight]);

  // Measure children height dynamically
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const measure = () => {
      const h = el.offsetHeight || 0;
      setContentHeight(h);
    };

    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(el);

    return () => ro.disconnect();
  }, []);

  const fullHeight = headerHeight + contentHeight;

  // When closed, only peek is visible
  const visibleHeight = open ? fullHeight : peekHeight;

  const closedOffset = fullHeight - peekHeight;

  // Animate when open/close changes
  useEffect(() => {
    if (!fullHeight) return; // wait until measurements are ready

    const y = open ? 0 : closedOffset;

    controls.start({
      y,
      transition: {
        type: "tween",
        duration: 0.3,
        ease: "linear",
      },
    });

    onToggle?.({ open, height: visibleHeight });
  }, [open, fullHeight, closedOffset, controls, visibleHeight, onToggle]);

  const handleDragEnd = (_: any, info: PanInfo) => {
    if (!fullHeight) return;

    const shouldClose = info.offset.y > closedOffset / 3;
    onOpenChange(!shouldClose);
  };

  const handleHeaderClick = () => onOpenChange(!open);

  return (
    <motion.div
      ref={wrapperRef}
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,

        transform: "translateZ(0)",
        WebkitTransform: "translateZ(0)",
        willChange: "transform",

        backfaceVisibility: "hidden",
        WebkitBackfaceVisibility: "hidden",
        opacity: props.hidden ? 0 : 1,
        pointerEvents: props.hidden ? "none" : "auto",
      }}
      animate={controls}
    >
      <Box
        sx={{
          background: colors.background.v1,
          overflow: "visible",
        }}
      >
        {/* HEADER (always visible) */}
        <PeekChildrenCollapsed
          ref={headerRef}
          onClick={handleHeaderClick}
          height={peekHeight}
          sx={{
            overflow: "visible", // ✅ VERY IMPORTANT
            position: "relative", // ✅ anchor for absolute handle

            "&::after": {
              transition: transitions.join(["opacity"]),
              opacity: open ? 0 : 1,
              top: 0,
            },
          }}
        >
          <Box
            sx={{
              width: open ? 104 : 36,
              height: 24, // ✅ FIXED HEIGHT (important)
              minHeight: 24, // ✅ prevents collapse
              margin: "0 auto",
              position: "absolute",

              bottom: open ? 0 : "100%",
              transform: "translateZ(0)", // ✅ ensure it renders
              WebkitTransform: "translateZ(0)",

              transition: transitions.join(
                ["width", "bottom"],
                transitions.linear,
              ),

              zIndex: 3, // ✅ above everything

              "&::before, &::after": {
                content: "''",
                position: "absolute",
                left: 0,
                backgroundColor: colors.text.v4,
                width: "100%",
                height: "1px",
              },
              "&::before": {
                bottom: "6px",
              },
              "&::after": {
                bottom: 0,
              },
            }}
          />
          {childrenCollapsed}
        </PeekChildrenCollapsed>

        {/* CHILDREN (hidden when closed) */}
        <Box
          ref={contentRef}
          sx={{
            // maxHeight: open ? 9999 : 0,
            // transition: "max-height 0.35s linear",
            // transition: "max-height 0.35s linear",
            overflow: "hidden",
            transition: transitions.join(["opacity"]),
            opacity: open ? 1 : 0,
            // p: open ? 2 : 0,
          }}
        >
          {children}
        </Box>
      </Box>
    </motion.div>
  );
};
